import { Box, Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMeasure } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { isBeneficiaryDesignationEntity } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { Operations as EntityDetailsDocumentOperations } from '@/modules/entities/details/graphql/EntityDetailPage.generated';
import { FIRST_DECEDENT_ID_QUERY_PARAM } from '@/modules/entityDiagram/EntityDiagram.constants';
import { $downloadStreamedFile } from '@/modules/files/utils/fileUtils';
import { FullScreenModalHouseholdChatHost } from '@/modules/household/chat/components/FullScreenModalHouseholdChatHost';
import { PresentationEntityActionsProviderBase } from '@/modules/presentation/clientPresentation/components/presentationEntityActions/PresentationEntityActionsProvider';
import {
  PresentationHoverActionsProvider,
  TOOLBAR_DISPLAY_DELAY_175MS,
} from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverActions.context';
import { PresentationEditingProvider } from '@/modules/presentation/context/PresentationEditing.context';
import { TenantPresentationConfigurationProvider } from '@/modules/presentation/context/TenantPresentationConfiguration.provider';
import {
  US_LETTER_ASPECT_RATIO_INVERSE,
  US_LETTER_SCALE_WIDTH_PX,
} from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';
import { getEntityGrantors } from '@/modules/presentation/slides/EntityBeneficiariesAndDispositionsSlide/EntityBeneficiariesAndDispositionsSlide.hooks';
import { COLORS } from '@/styles/tokens/colors';
import { AugmentedUpdateEntityInput } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { useUpdateEntityMutation } from '../../graphql/UpdateEntity.generated';
import { isEntityTimeBound } from '../../utils/getEntityNotesForDeath';
import { ExportEntityBundleModal } from '../ExportEntityBundleModal/ExportEntityBundleModal';
import { EntitySlideType } from '../ExportEntityBundleModal/ExportEntityBundleModal.types';
import {
  EntityPreviewModalHeader,
  EntityPreviewModalSlideDisplay,
} from './EntityPreviewModal.components';
import {
  EntityPreviewModalPaginationProvider,
  useGuardedEntityPreviewModalPaginationContext,
} from './EntityPreviewModal.paginationContext';
import { EntityPreviewModalFormShape } from './EntityPreviewModal.types';
import { makeUpdateEntityInput } from './EntityPreviewModal.utils';
import { Operations as EntityPreviewModalDocumentOperations } from './graphql/EntityPreviewModal.generated';
import {
  EntityPreviewModal_EntityFragment,
  EntityPreviewModalQueryVariables,
  useEntityPreviewModalQuery,
} from './graphql/EntityPreviewModal.generated';
import { useExportEntityBundleV2Mutation } from './graphql/ExportEntityBundleModal.generated';

export interface EntityPreviewModalProps {
  onClose: () => void;
  entityId: string;
  householdId: string;
  initialSlide?: EntitySlideType;
  deathOrderId?: string | null | undefined;
}

export function EntityPreviewModal({
  entityId,
  householdId,
  onClose,
  initialSlide = EntitySlideType.CoverSlide,
  deathOrderId: externalDeathOrderId,
}: EntityPreviewModalProps) {
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const form = useForm<EntityPreviewModalFormShape>({
    defaultValues: {
      deathOrderId: null,
      isTimeBoundEntity: false,
      shouldDisplayNotes: true,
      isBeneficiaryDesignationEntity: false,
    },
  });

  const { setValue, getValues, handleSubmit } = form;

  const [searchParams] = useSearchParams();
  const firstDecedentId = useMemo(
    () => searchParams.get(FIRST_DECEDENT_ID_QUERY_PARAM),
    [searchParams]
  );

  const [
    { isModalOpen: isExportModalOpen, data: exportModalData },
    { openModal: openExportModal, closeModal: closeExportModal },
  ] = useModalState<{
    deathOrderId: string | null;
    includeNotesOnDiagramSlide: boolean;
  }>();

  const variables: EntityPreviewModalQueryVariables = useMemo(
    () => ({
      entityWhere: {
        id: entityId,
      },
      householdId,
    }),
    [entityId, householdId]
  );

  const {
    data,
    loading,
    error: entityFetchError,
    refetch,
  } = useEntityPreviewModalQuery({
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  useReportError('EntityPreviewModal', entityFetchError, {
    entityId,
    householdId,
  });

  // Capability + refresh primitive shared with the entity-diagram editing chrome
  // (which reads it to enable its hover CTAs and refresh after a save). Apollo's
  // `refetch` is stable, so this value is referentially stable across renders.
  const canEdit = useCanEditHouseholdData();
  const editingValue = useMemo(
    () => ({
      canEdit,
      householdId,
      onRefresh: () => {
        void refetch();
      },
    }),
    [canEdit, householdId, refetch]
  );

  const entity = useMemo(
    () => first(getNodes(data?.entities)) ?? null,
    [data?.entities]
  );

  const usesBeneficiaryDesignations = isBeneficiaryDesignationEntity(
    entity?.kind
  );

  useEffect(() => {
    if (!entity) return;

    const isTimeBoundEntity = isEntityTimeBound(entity);
    const grantors = getEntityGrantors(
      entity?.household.possiblePrimaryClients ?? []
    );

    let deathOrderId: string | null =
      externalDeathOrderId ?? firstDecedentId ?? first(grantors)?.id ?? null;
    if (isTimeBoundEntity) {
      deathOrderId = null;
    }
    setValue('isTimeBoundEntity', isTimeBoundEntity);
    setValue('deathOrderId', deathOrderId);
    setValue('isBeneficiaryDesignationEntity', usesBeneficiaryDesignations);
  }, [
    entity,
    externalDeathOrderId,
    firstDecedentId,
    usesBeneficiaryDesignations,
    setValue,
  ]);

  const [updateEntity, { error: updateEntityError }] = useUpdateEntityMutation({
    refetchQueries: [
      EntityPreviewModalDocumentOperations.Query.EntityPreviewModal,
      EntityDetailsDocumentOperations.Query.EntityDetails,
    ],
    onCompleted: () => {
      showFeedback('Notes updated', {
        variant: 'success',
      });
    },
    onError: () => {
      showFeedback('Error updating notes', {
        variant: 'error',
      });
    },
  });

  const [exportEntityBundle, { loading: exportLoading, error: exportError }] =
    useExportEntityBundleV2Mutation({
      onError: () => {
        showFeedback(`We weren't able to generate the PDF. Please try again.`);
      },
    });

  useReportError('EntityPreviewModal', exportError, {
    entityId,
    householdId,
  });

  const onExport = async (pathToPrint: string) => {
    if (!entity) {
      showFeedback('Entity not found', { variant: 'error' });
      reportError('Entity not found when trying to export entity bundle');
      return;
    }

    await exportEntityBundle({
      variables: {
        householdId,
        pathToPrint,
      },
      onCompleted: async (data) => {
        const token = data.printV2?.token;
        if (token) {
          try {
            await $downloadStreamedFile(
              `/api/download/file?token=${token}`,
              entity?.subtype.displayName
            );
            trackUserEvent('entity exported', {
              entityId,
              householdId,
            });
            showFeedback('PDF generated successfully', {
              variant: 'success',
            });
          } catch (err) {
            showFeedback('Failed to download PDF. Please try again.');
          }
        } else {
          showFeedback('Failed to download PDF. Please try again.');
        }
      },
    });
  };

  useReportError('EntityPreviewModal', updateEntityError, {
    entityId,
    householdId,
  });

  const handleExportClick = handleSubmit((data) => {
    openExportModal({
      deathOrderId: data.deathOrderId,
      includeNotesOnDiagramSlide: data.shouldDisplayNotes,
    });
  });

  const onUpdateNotes = useCallback(
    async (newNotes: string) => {
      if (!entity) {
        diagnostics.error('Called onUpdateNotes before entity was loaded');
        return;
      }
      const input: AugmentedUpdateEntityInput = makeUpdateEntityInput({
        entityId,
        householdId,
        newNotes,
        oldNotes: entity?.deathOrderNotes ?? [],
        activeDeathOrderId: getValues('deathOrderId'),
        isTimeBoundEntity: getValues('isTimeBoundEntity'),
        syncDeathOrderNotes: entity.syncDeathOrderNotes,
      });

      trackUserEvent('entity_notes saved');
      await updateEntity({
        variables: {
          input,
        },
      });
    },
    [entity, entityId, getValues, householdId, trackUserEvent, updateEntity]
  );

  return (
    <TenantPresentationConfigurationProvider
      subBrandId={
        loading && !data ? undefined : (entity?.household.subBrand?.id ?? null)
      }
    >
      <FormProvider {...form}>
        <EntityPreviewModalPaginationProvider
          initialSlide={initialSlide}
          entity={entity}
        >
          <FullScreenModal isOpen onClose={onClose}>
            <LoadingOverlay open={loading} />
            <FullScreenModalHouseholdChatHost entryPointBottomOffset={16}>
              {({ householdChatPane }) => (
                <Stack height="100%" width="100%" direction="row" minWidth={0}>
                  <Stack height="100%" flex={1} minWidth={0}>
                    {isExportModalOpen && (
                      <ExportEntityBundleModal
                        entityId={entityId}
                        householdId={householdId}
                        onClose={closeExportModal}
                        onExport={onExport}
                        exportLoading={exportLoading}
                        {...exportModalData}
                      />
                    )}
                    <EntityPreviewModalHeader
                      onClose={onClose}
                      onExport={handleExportClick}
                      exportLoading={exportLoading}
                    />
                    <HouseholdPermissionAwareDisabledProvider
                      forceDisabled={loading}
                    >
                      {/* Bring the presentation designer's hover states + edit
                    toolbars to the single-entity export. The actions provider
                    owns the edit modals and refreshes the slide via the modal's
                    own Apollo refetch after a save; the editing context lets the
                    shared entity-diagram chrome do the same. */}
                      <PresentationEditingProvider value={editingValue}>
                        <PresentationHoverActionsProvider
                          toolbarDisplayDelayMS={TOOLBAR_DISPLAY_DELAY_175MS}
                        >
                          {/* No `onAfterMutation` (the designer uses it to refetch the
                        entity-diagram query after cover/list edits): the preview
                        shows one slide at a time and the diagram query is
                        `no-cache`, so a cover/beneficiaries/insurance edit happens
                        while the diagram is unmounted and it refetches fresh on the
                        next navigation. The diagram slide's own nested provider
                        still wires `onAfterMutation` for edits from its tiles. */}
                          <PresentationEntityActionsProviderBase
                            householdId={householdId}
                            onRefresh={editingValue.onRefresh}
                          >
                            <EntityPreviewModalInner
                              entity={entity}
                              onUpdateNotes={onUpdateNotes}
                              error={entityFetchError}
                              loading={loading}
                              refetch={refetch}
                            />
                          </PresentationEntityActionsProviderBase>
                        </PresentationHoverActionsProvider>
                      </PresentationEditingProvider>
                    </HouseholdPermissionAwareDisabledProvider>
                  </Stack>
                  {householdChatPane}
                </Stack>
              )}
            </FullScreenModalHouseholdChatHost>
          </FullScreenModal>
        </EntityPreviewModalPaginationProvider>
      </FormProvider>
    </TenantPresentationConfigurationProvider>
  );
}

interface EntityPreviewModalInnerProps {
  entity: EntityPreviewModal_EntityFragment | null;
  onUpdateNotes: (newNotes: string) => Promise<void>;
  error: Error | undefined;
  loading: boolean;
  refetch: () => void;
}

function EntityPreviewModalInner({
  entity,
  onUpdateNotes,
  loading,
  error,
  refetch,
}: EntityPreviewModalInnerProps) {
  const { nextSlide, previousSlide, nextSlideEnabled, previousSlideEnabled } =
    useGuardedEntityPreviewModalPaginationContext();
  const [innerRef, { height }] = useMeasure();

  const scaledWidth = height / US_LETTER_ASPECT_RATIO_INVERSE;

  if (loading) {
    return null;
  }

  if (error || !entity) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        spacing={2}
      >
        <Typography variant="title-l" color={COLORS.FUNCTIONAL.ERROR.DEFAULT}>
          Could not load entity data
        </Typography>
        <Button variant="secondary" onClick={() => refetch()} size="sm">
          Reload data
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      width="100%"
      alignItems="center"
      data-testid="outer"
      direction="row"
      justifyContent="center"
      flex="2 2 100%"
    >
      <Stack
        height="100%"
        alignItems="flex-end"
        justifyContent="center"
        flex="1 0 10%"
        sx={{ pr: 3 }}
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={previousSlide}
          disabled={!previousSlideEnabled}
          square
        >
          <ChevronLeftIcon size={16} />
        </Button>
      </Stack>
      <Box
        ref={innerRef}
        sx={{
          /*
           * Outer box to fill the container horizontally
           */
          // this flex lets the slide content scale to a factor of 1.0
          // (full size) without pushing the navigation arrows away
          flex: `0 1 ${scaledWidth}px`,
          maxWidth: US_LETTER_SCALE_WIDTH_PX,
          py: 2,
          position: 'relative',
          height: '100%',
        }}
      >
        <Stack
          position="absolute"
          justifyContent="center"
          sx={{
            /*
             * Inner box to expand to the full available height
             */
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <EntityPreviewModalSlideDisplay
            entity={entity}
            onUpdateNotes={onUpdateNotes}
          />
        </Stack>
      </Box>
      <Stack
        height="100%"
        alignItems="flex-start"
        justifyContent="center"
        flex="1 0 10%"
        sx={{ pl: 3 }}
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={nextSlide}
          disabled={!nextSlideEnabled}
          square
        >
          <ChevronRightIcon size={16} />
        </Button>
      </Stack>
    </Stack>
  );
}
